import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { forkJoin, Observable, EMPTY } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import {
    ModulesReportsApplicationReportCategoriesReportCategoryResponse,
    ModulesReportsApplicationReportParameterDependenciesReportParameterDependencyResponse,
    ModulesReportsApplicationReportParameterLinksReportParameterLinkResponse,
    ModulesReportsApplicationReportParametersReportParameterResponse,
    ModulesReportsApplicationReportsReportResponse,
    ReportsService
} from '../../api-client';
import { LoggingService } from '../../services/shared/logging/logging.service';

@Injectable()
export class ReportsStore {
    private readonly reportsApi = inject(ReportsService);
    private readonly loggingService = inject(LoggingService);

    readonly categories: WritableSignal<ModulesReportsApplicationReportCategoriesReportCategoryResponse[]> = signal([]);
    readonly reports: WritableSignal<ModulesReportsApplicationReportsReportResponse[]> = signal([]);
    readonly parameters: WritableSignal<ModulesReportsApplicationReportParametersReportParameterResponse[]> = signal([]);
    readonly parameterLinks: WritableSignal<ModulesReportsApplicationReportParameterLinksReportParameterLinkResponse[]> = signal([]);
    readonly parameterDependencies: WritableSignal<ModulesReportsApplicationReportParameterDependenciesReportParameterDependencyResponse[]> = signal([]);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);

    loadCatalog(): void {
        this.loading.set(true);
        this.error.set(null);

        forkJoin({
            categories: this.reportsApi.getReportCategoriesEndpoint(),
            reports: this.reportsApi.getReportsEndpoint(),
            parameters: this.reportsApi.getReportParametersEndpoint(),
            parameterLinks: this.reportsApi.getReportParameterLinksEndpoint(),
            parameterDependencies: this.reportsApi.getReportParameterDependenciesEndpoint()
        })
            .pipe(
                catchError(error => {
                    this.loggingService.error('ReportsStore', 'Failed to load reporting catalog', error);
                    this.error.set('Unable to load reporting catalog. Please try again.');
                    return EMPTY;
                }),
                finalize(() => this.loading.set(false))
            )
            .subscribe(result => {
                if (!result) {
                    return;
                }

                this.categories.set(result.categories ? result.categories : []);
                this.reports.set(result.reports ? result.reports : []);
                this.parameters.set(result.parameters ? result.parameters : []);
                this.parameterLinks.set(result.parameterLinks ? result.parameterLinks : []);
                this.parameterDependencies.set(result.parameterDependencies ? result.parameterDependencies : []);
            });
    }
}
